import { useEffect, useRef, useState } from 'react';

import { ADSENSE_CLIENT_ID, ADSENSE_SLOT_ID } from '../lib/ads';

/**
 * Renders a single AdSense ad slot. Returns null when AdSense isn't
 * configured (env vars unset) so dev builds get a clean UI without the
 * placeholder.
 *
 * The `<ins class="adsbygoogle">` element is the slot AdSense fills
 * with a creative iframe; calling `adsbygoogle.push({})` once on
 * mount tells the runtime to activate this slot. We push from a ref
 * effect so we activate exactly once per slot lifecycle (StrictMode
 * mounts the component twice in dev; the ref guard prevents a double
 * push that AdSense logs as a warning).
 *
 * Chrome (border + background) is suppressed until AdSense actually
 * fills the slot — observed via the `data-ad-status="filled"` attribute
 * AdSense sets on the `<ins>` once a creative is delivered. Empty /
 * unfilled / blocked impressions therefore render as nothing, not as a
 * visible empty card.
 */
export function AdBanner(): JSX.Element | null {
  const pushedRef = useRef(false);
  const insRef = useRef<HTMLModElement>(null);
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    if (pushedRef.current) return;
    if (ADSENSE_CLIENT_ID === undefined || ADSENSE_SLOT_ID === undefined) return;
    if (!Array.isArray(window.adsbygoogle)) return;
    try {
      window.adsbygoogle.push({});
      pushedRef.current = true;
    } catch {
      // AdSense init can race the script load; the script will
      // pick up unactivated slots automatically when it loads, so
      // a thrown push is non-fatal.
    }
  }, []);

  // Watch for AdSense to set data-ad-status="filled" on the <ins>. The
  // attribute is the only public signal that a creative landed; switch
  // chrome on at that moment, ignore "unfilled" / never-set so the box
  // stays invisible when no ad rendered.
  useEffect(() => {
    const ins = insRef.current;
    if (ins === null) return;
    if (ins.getAttribute('data-ad-status') === 'filled') {
      setFilled(true);
      return;
    }
    const observer = new MutationObserver(() => {
      if (ins.getAttribute('data-ad-status') === 'filled') {
        setFilled(true);
        observer.disconnect();
      }
    });
    observer.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] });
    return () => observer.disconnect();
  }, []);

  if (ADSENSE_CLIENT_ID === undefined || ADSENSE_SLOT_ID === undefined) {
    return null;
  }

  return (
    <div
      className={
        filled
          ? 'overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
          : 'overflow-hidden'
      }
    >
      <ins
        ref={insRef}
        className="adsbygoogle block"
        style={{ display: 'block' }}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={ADSENSE_SLOT_ID}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
